import React, { useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@src/components/Tooltip';
import { BREAKPOINTS } from '@src/styles/config';
import theme from '@src/styles/theme';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import { PACK_DURATION, PACKAGE_EXPIRED } from '@src/constants/package';
import { checkVietNam } from '@src/utils/checkCountry';
import { getPackageCurrentPrice } from '@src/services/package';
import { StyledSubscriptionInfo } from './index.style';

const SubscriptionInfo = ({ buyPackage }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));
  const isVietNam = checkVietNam();
  const [openPopupDetailInfo, setOpenPopupDetailInfo] = useState(false);

  const { expiresIn } = buyPackage;
  const { price, usdPrice } = getPackageCurrentPrice(buyPackage, true);

  const nextPaymentDate = () => {
    let expDate = moment().toISOString();

    if (buyPackage?.expiresIn === PACK_DURATION?.YEAR)
      expDate = moment().add(1, 'year').toISOString();
    if (buyPackage?.expiresIn === PACK_DURATION?.MONTH)
      expDate = moment().add(1, 'month').toISOString();

    return moment(expDate).format('DD/MM/YYYY');
  };

  const subscriptionCycle = () => {
    const isYearPackage = [PACK_DURATION.YEAR, PACKAGE_EXPIRED.YEARLY].includes(
      expiresIn,
    );

    return isYearPackage ? `1 ${t('year')}` : `1 ${t('month')}`;
  };

  const handleShowPopupDetailInfo = () => {
    if (!isMobile) return;
    setOpenPopupDetailInfo(true);
  };

  return (
    <StyledSubscriptionInfo>
      <Typography className="title">{t('nextTimeRenewal')}</Typography>
      <Box className="content">
        <Box display="flex" justifyContent="space-between">
          <Typography className="text-content-bold">
            {t('nextPayment')}
          </Typography>
          <Typography className="text-content-bold">
            {nextPaymentDate()}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Typography className="text-content-bold">
            {t('subscriptionCycle')}
          </Typography>
          <Typography className="text-content-bold">
            {subscriptionCycle()}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap="4px">
            <Typography className="text-content-bold">
              {t('amountToBePaid')}
            </Typography>
            <div>
              <Tooltip
                arrow
                title={
                  <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(t('subscriptionInfoNote'), {
                        allowedTags: ['div'],
                        allowedAttributes: {
                          div: ['style'],
                        },
                      }),
                    }}
                  />
                }
                placement="top"
              >
                <IconButton>
                  <InfoIcon
                    onClick={() => handleShowPopupDetailInfo()}
                    className="subscription-info-note"
                  />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
          <div>
            {!isVietNam && (
              <span className="discount-unit-price font-size-16">$</span>
            )}
            {isVietNam && (
              <span className="discount-unit-price underline font-size-16">
                đ
              </span>
            )}
            {isVietNam
              ? price?.toLocaleString(language)
              : usdPrice?.toLocaleString(language)}
          </div>
        </Box>
      </Box>
      <NotificationDialogV3
        name={t('detailedInformation')}
        title={t('detailedInformation')}
        description={t('subscriptionInfoNote')}
        open={openPopupDetailInfo}
        onClose={() => setOpenPopupDetailInfo(false)}
        isAutoRenew
        variant="null"
        actionComponent={
          <Button
            onClick={() => setOpenPopupDetailInfo(false)}
            className="btn-close-popup"
            variant="contained"
          >
            {t('gotIt')}
          </Button>
        }
      />
    </StyledSubscriptionInfo>
  );
};

export default SubscriptionInfo;
